import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import styled from "styled-components";
import Box from "@sproutsocial/seeds-react-box";
import Text from "@sproutsocial/seeds-react-text";
import {
  COMMON,
  FLEXBOX,
  BORDER,
  LAYOUT,
} from "@sproutsocial/seeds-react-system-props";
import type { TypeModalHeaderProps } from "../ModalTypes";
import { useDragContext } from "./ModalContent";

interface HeaderProps {
  draggable?: boolean;
  isDragging?: boolean;
}

/**
 * Base styled header component for custom modal headers.
 *
 * Use this component when you need complete control over the header layout
 * and don't want to use the slot-based ModalHeader component.
 *
 * @example
 * <ModalCustomHeader>
 *   <YourCustomHeaderContent />
 * </ModalCustomHeader>
 */
export const ModalCustomHeader = styled(Box).withConfig({
  shouldForwardProp: (prop) => !["draggable", "isDragging"].includes(prop),
})<HeaderProps>`
  font-family: ${(props) => props.theme.fontFamily};
  padding: ${(props) => props.theme.space[400]};
  padding-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;

  /* Draggable cursor styling */
  ${(props) =>
    props.draggable &&
    `
    cursor: ${props.isDragging ? "grabbing" : "grab"};
    user-select: none;
  `}

  ${COMMON}
  ${FLEXBOX}
  ${BORDER}
  ${LAYOUT}
`;

ModalCustomHeader.displayName = "ModalCustomHeader";

/**
 * Modal header component with title and subtitle slots.
 *
 * This component only supports slot-based rendering via title and subtitle props.
 * For custom header layouts, use ModalCustomHeader instead.
 *
 * @example
 * <ModalHeader title="Delete Item" subtitle="This action cannot be undone" />
 */
export const ModalHeader = (props: TypeModalHeaderProps) => {
  const {
    title,
    subtitle,
    titleProps = {},
    subtitleProps = {},
    ...rest
  } = props;

  const dragContext = useDragContext();
  const isDraggable = dragContext?.draggable ?? false;

  return (
    <ModalCustomHeader
      data-slot="modal-header"
      data-qa-modal-header
      {...rest}
      onMouseDown={isDraggable ? dragContext?.onHeaderMouseDown : undefined}
      draggable={isDraggable}
      isDragging={dragContext?.isDragging}
    >
      <Box>
        {title && (
          <Dialog.Title asChild {...titleProps}>
            <Text.Headline>{title}</Text.Headline>
          </Dialog.Title>
        )}
        {subtitle && (
          <Dialog.Description asChild {...subtitleProps}>
            <Text as="div" fontSize={200}>
              {subtitle}
            </Text>
          </Dialog.Description>
        )}
      </Box>
    </ModalCustomHeader>
  );
};

ModalHeader.displayName = "ModalHeader";
